import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ApiError, api, errorText, type DeviceDetail, type SectionSnapshot } from "../api";

// Gemeinsame Refresh-/Fehler-/Stand-Logik aller Sektionen der Detailseite.
// Eine neue Sektion ist damit ein useSection(...) + eine SectionCard — mehr
// nicht (siehe docs/device-management.md „Erweiterbarkeit“).

/**
 * Ab wann eine Sektion beim Öffnen selbst nachlädt. Der eine Ort dafür.
 *
 * Kein Intervall und kein Hintergrund-Refresh: geholt wird einmal, wenn ein
 * Mensch die Sektion aufschlägt, und nur wenn der Stand älter ist. Damit kostet
 * eine geöffnete Detailseite höchstens einen Frame je Tab und Fenster — und
 * dank `if_changed` ist der Rückweg bei Gleichstand ~60 Byte
 * (docs/device-management.md → „Nachladen beim Öffnen“).
 */
export const SECTION_MAX_AGE_MS = 5 * 60 * 1000;

/**
 * Frisch geholte Snapshots zurück an die Detailseite.
 *
 * Ohne das vergisst ein Tabwechsel den Stand: die Sektion wird beim Verlassen
 * ausgehängt und käme mit dem alten `cached` aus `GET /api/devices/<id>`
 * zurück — also mit einem Zeitstempel, der sofort wieder als „zu alt“ gilt.
 * Der Router zahlte den Tabwechsel dann in Funkverkehr.
 */
export const SectionSink = createContext<(section: string, snap: SectionSnapshot) => void>(
  () => {},
);

export interface SectionHandle<T> {
  /** Zuletzt bekannter Stand (Cache oder frisch abgefragt). */
  data: T | null;
  /** Wann diese Daten vom Router kamen (ISO) — `null` = noch nie abgefragt. */
  fetchedAt: string | null;
  loading: boolean;
  /** Echter Fehler (kein Offline) — Offline ist ein eigener, milder Zustand. */
  error: string | null;
  /** Letzter Refresh scheiterte, weil der Router nicht erreichbar war (503). */
  offline: boolean;
  /** Live-Abfrage — kostet Mobilfunkvolumen. */
  refresh: () => Promise<void>;
  /** Nachladen nur, wenn der Stand zu alt ist — für Sektionen, die eine andere
   *  mitliest (die Firewall braucht die Geräteliste erst im Dialog). */
  ensureFresh: () => void;
  /** Lokal setzen, wenn eine Aktion die Sektion bereits mitliefert. */
  patch: (data: T, fetchedAt?: string) => void;
}

interface Snap<T> {
  data: T | null;
  fetchedAt: string | null;
}

/**
 * Von den zwei Quellen gewinnt die jüngere: der Cache aus dem Detail-Load und
 * das, was diese Sektion selbst geholt hat. Beides in einen Zustand zu mischen
 * hieße, im Effekt auf eine geänderte Prop hin Zustand zu setzen — und für
 * einen Renderlauf stünde der ältere Stand da.
 */
function newerSnapshot<T>(cached: SectionSnapshot | undefined, fetched: Snap<T> | null): Snap<T> {
  if (!cached) return fetched ?? { data: null, fetchedAt: null };
  if (fetched?.fetchedAt && cached.fetchedAt <= fetched.fetchedAt) return fetched;
  return { data: cached.data as T, fetchedAt: cached.fetchedAt };
}

function isStale(fetchedAt: string | null): boolean {
  if (!fetchedAt) return true;
  const age = Date.now() - new Date(fetchedAt).getTime();
  return Number.isNaN(age) || age > SECTION_MAX_AGE_MS;
}

/**
 * Eine Sektion an den Server binden.
 *
 * Beim Aufschlagen zeigt der Hook sofort den Server-Cache und holt genau dann
 * selbst nach, wenn der Router online und der Stand älter als
 * `SECTION_MAX_AGE_MS` ist. Nur die gerade sichtbare Sektion — die anderen sind
 * gar nicht eingehängt.
 *
 * `lazy` schaltet das Nachladen beim Aufschlagen ab: die Sektion hängt dann
 * nur am Cache, bis jemand `ensureFresh()` ruft. So kann eine Sektion die Daten
 * einer anderen mitbenutzen, ohne dass jeder Besuch einen Frame kostet.
 */
export function useSection<T>(
  device: DeviceDetail,
  section: string,
  { lazy = false }: { lazy?: boolean } = {},
): SectionHandle<T> {
  const deviceId = device.id;
  const cached = device.sections[section];
  const publish = useContext(SectionSink);

  // Nur das, was diese Sektion selbst geholt oder per `patch` gesetzt hat. Der
  // Cache der Detailseite bleibt daneben stehen; welcher gilt, entscheidet bei
  // jedem Render der Zeitstempel.
  const [fetched, setFetched] = useState<Snap<T> | null>(null);
  // Gemerkt, damit der Stand seine Identität behält: sonst wäre er in jedem
  // Renderlauf ein neues Objekt und der Ref-Effekt unten liefe jedes Mal mit.
  const snap = useMemo(() => newerSnapshot<T>(cached, fetched), [cached, fetched]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setOffline(false);
    try {
      const r = await api.refreshSection(deviceId, section);
      setFetched({ data: r.data as T, fetchedAt: r.fetchedAt });
      publish(section, { data: r.data, fetchedAt: r.fetchedAt });
    } catch (e) {
      // 503 = Router offline/Timeout → Cache bleibt stehen, nur Hinweis.
      if (e instanceof ApiError && e.isOffline) setOffline(true);
      else setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [deviceId, section, publish]);

  // Nachladen beim Öffnen. Der Ref hält den zuletzt gerenderten Stand fest,
  // damit der Effekt darunter nicht an `snap` hängt und sich selbst neu
  // auslöst. Geschrieben wird er in einem Effekt, nicht im Render — Effekte
  // laufen in der Reihenfolge ihrer Deklaration, der Wert steht also, bevor
  // der Effekt darunter ihn liest.
  const current = useRef(snap);
  useEffect(() => {
    current.current = snap;
  }, [snap]);
  const ensureFresh = useCallback(() => {
    if (!device.online || loading || !isStale(current.current.fetchedAt)) return;
    void refresh();
  }, [device.online, loading, refresh]);

  const pulled = useRef(false);
  useEffect(() => {
    if (lazy || pulled.current || !device.online) return;
    pulled.current = true;
    ensureFresh();
  }, [lazy, device.online, ensureFresh]);

  const patch = useCallback(
    (data: T, fetchedAt?: string) => {
      const stamp = fetchedAt ?? new Date().toISOString();
      setFetched({ data, fetchedAt: stamp });
      publish(section, { data, fetchedAt: stamp });
    },
    [publish, section],
  );

  return {
    data: snap.data,
    fetchedAt: snap.fetchedAt,
    loading,
    error,
    offline,
    refresh,
    ensureFresh,
    patch,
  };
}
